/**
 * Черновик калькулятора в localStorage: ключ, владелец, чтение и запись.
 *
 * Отдельный модуль, а не экспорт из файла с компонентом, потому что у черновика
 * два потребителя: сам виджет (пишет и восстанавливает) и модульный слой
 * (`mountRemote.mount` называет владельца, `disposeModuleState` его снимает).
 * Плюс fast refresh: экспорт не-компонента из файла с компонентом ломает
 * react-refresh/only-export-components.
 *
 * Доступ к ключу идёт ТОЛЬКО через функции этого файла. Прямой
 * `localStorage.getItem(CALCULATOR_DRAFT_KEY)` в обход `readCalculatorDraft()`
 * означал бы чтение мимо проверки владельца — то есть ровно ту утечку, ради
 * которой владелец и заведён.
 */

export const CALCULATOR_DRAFT_KEY = 'idea_calc_data';

/**
 * Откуда открыт калькулятор — и, следовательно, чей черновик здесь уместен.
 *
 * `productCard` — платформа открыла модуль из карточки товара. Черновика у такого
 * монтирования НЕТ вовсе: карточка и есть источник истины, и открытие карточки B
 * обязано показать товар B, а не ASIN и конкурентов, оставшихся от карточки A
 * (наблюдалось на платформе 2026-08-24 — слот черновика один на все монтирования,
 * и предыдущая работа перетекала в следующую карточку и в общий калькулятор).
 * Незаписанную работу здесь хранит история расчётов по `product_id`, а не localStorage.
 *
 * `standalone` — общий калькулятор (`/apps/profitCalculator` и обычная вкладка).
 * Своей карточки у него нет, терять работу при случайном обновлении страницы
 * нельзя — здесь черновик и живёт.
 */
export type CalculatorDraftWorkspace = 'standalone' | 'productCard';

/**
 * Владелец и рабочее место черновика.
 *
 * Владелец — `user.id` из `HostContext` в embed и `null` в standalone, где вкладка
 * целиком принадлежит одному пользователю.
 *
 * Значение живёт в модульной области, то есть ДОЛЬШЕ дерева, и снимается в
 * `disposeModuleState()` вместе с остальным состоянием жизни модуля.
 */
let draftOwnerId: string | null = null;
let draftWorkspace: CalculatorDraftWorkspace = 'standalone';

/** Зовётся из `mountRemote.mount` (embed) и `disposeModuleState` (сброс в standalone). */
export function setCalculatorDraftScope(
  ownerId: string | null,
  workspace: CalculatorDraftWorkspace,
): void {
  draftOwnerId = ownerId;
  draftWorkspace = workspace;
}

export interface CalculatorDraftDims {
  l: number | null;
  w: number | null;
  h: number | null;
}

/**
 * Содержимое черновика. Форма поля `data` принадлежит `Calculator.tsx`: это
 * разобранный JSON из localStorage, то есть непроверенные данные, и приводит их
 * к своему состоянию тот, кто это состояние объявляет.
 */
export interface CalculatorDraft {
  data?: Record<string, unknown>;
  asin?: string;
  competitorAsins?: string[];
  dims?: CalculatorDraftDims;
  couponPromoEnabled?: boolean;
  purchasePriceCny?: number | null;
  keepaLoaded?: boolean;
  timestamp?: number;
}

interface StoredDraft extends CalculatorDraft {
  /**
   * Владелец на момент записи. Поля нет вовсе — черновик записан версией до
   * штампа, а тогда единственным режимом с черновиком был standalone.
   */
  ownerId?: string | null;
}

/**
 * Черновик текущего владельца — или `null`, если владелец другой.
 *
 * Почему штамп, а не стирание на `unmount`. Черновик — это ASIN, цены и партия
 * конкретного пользователя, и в embed модуль монтируется и размонтируется в
 * одной вкладке под РАЗНЫМИ людьми: показать экономику одного клиента другому
 * нельзя. Раньше личность была известна только при монтировании, поэтому
 * черновик стирался на каждом `unmount` — и вместе с чужим терялся свой,
 * а вернувшийся пользователь начинал с нуля. Теперь личность приходит
 * `HostContext.user.id` и штампуется в запись: чужому черновик не виден, свой
 * переживает размонтирование.
 *
 * Плата за это — черновик остаётся в localStorage вкладки. От чужих глаз его
 * защищает штамп, а не стирание; иначе требование «свой черновик переживает
 * unmount» неисполнимо в принципе.
 *
 * Слот один: кто пишет последним, тот и владеет записью. Ключ на владельца
 * (`idea_calc_data:<id>`) сохранял бы черновики всех, кто когда-либо открывал
 * модуль в этой вкладке, — неограниченный рост хранилища ради случая, которого
 * требование не ставит.
 */
export function readCalculatorDraft(): CalculatorDraft | null {
  // Открыто из карточки товара — черновика здесь не существует по определению
  // (см. CalculatorDraftWorkspace). Не «не нашли», а «не спрашиваем».
  if (draftWorkspace === 'productCard') return null;

  let raw: string | null;
  try {
    raw = localStorage.getItem(CALCULATOR_DRAFT_KEY);
  } catch {
    // Приватный режим / отключённое хранилище: читать неоткуда, и это не
    // ошибка приложения — черновик всего лишь удобство.
    return null;
  }
  if (!raw) return null;

  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    // Испорченный JSON — не черновик. Сообщать некому: чтение идёт в
    // инициализаторе useState первого рендера, UI ещё нет.
    return null;
  }
  if (stored === null || typeof stored !== 'object') return null;

  // `?? null`: у записей без штампа владельца нет, а сделаны они могли быть
  // только в standalone — где владельца нет и сейчас.
  const draft = stored as StoredDraft;
  if ((draft.ownerId ?? null) !== draftOwnerId) return null;
  return draft;
}

/**
 * Пишет черновик от имени текущего владельца.
 *
 * Из карточки товара не пишет ничего — и это половина того же правила, что и в
 * `readCalculatorDraft`. Слот в localStorage один: писала бы карточка, её ASIN и
 * конкуренты всплыли бы в общем калькуляторе, который черновик как раз читает.
 */
export function writeCalculatorDraft(draft: CalculatorDraft): void {
  if (draftWorkspace === 'productCard') return;

  const stored: StoredDraft = { ...draft, ownerId: draftOwnerId };
  try {
    localStorage.setItem(CALCULATOR_DRAFT_KEY, JSON.stringify(stored));
  } catch {
    // Квота исчерпана / приватный режим. Расчёт от этого не страдает: черновик
    // восстанавливает поля после перезагрузки, а не хранит результат.
  }
}
